import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import OtpInput from "react-otp-input";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import QuoteSlider from "./QuoteSlider";
import axios from "axios";
import apiConfig from "./apiConfig";
import "./style.scss";

const quotes = [
  "Easily discover and quickly benefit from the most advanced technologies.",
  "Expert-level insights at your fingertips.",
  "Expedite planning, fieldwork and completion procedures.",
  "Elevate your expertise and confidence.",
  "AI-powered audits: Save time, increase profitability, and improve quality.",
];

function TwoFactorOTP() {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitCooldown, setSubmitCooldown] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const handleOtpChange = (otpValue) => {
    if (/^\d{0,6}$/.test(otpValue)) {
      setOtp(otpValue);
    }
  };

  // Helper function to start a cooldown timer
  const startCooldown = (setCooldown, seconds = 15) => {
    setCooldown(seconds);
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOTP = async () => {
    // Prevent multiple resend clicks during cooldown
    if (resendCooldown > 0) return;

    if (!email) {
      toast.error("Email or UserID is missing. Cannot resend OTP.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    // Start resend cooldown
    startCooldown(setResendCooldown);

    try {
      console.log("Resending OTP for email:", email);
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/resendOtp`,
        { email },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Resend OTP API Response:", response.data);

      if (response.data.success) {
        toast.success(
          response.data.message || "OTP resent successfully! Check your email.",
          { position: toast.POSITION.TOP_RIGHT }
        );
      } else {
        toast.error(response.data.message || "Failed to resend OTP.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      const errorMessage =
        error.response?.data?.message || "Something went wrong. Please try again.";
      toast.error(errorMessage, { position: toast.POSITION.TOP_RIGHT });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Check if OTP is empty
    if (!otp.trim()) {
      toast.error("Please enter OTP", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    // Prevent multiple submit clicks during cooldown
    if (submitCooldown > 0) return;

    startCooldown(setSubmitCooldown);
    setLoading(true);

    try {
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/verifyOtpWithJwt`,
        {
          email: email,
          otp: otp,
        },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
          },
        }
      );

      if (response.data.success) {

        localStorage.setItem("authToken", response.data.token);
        sessionStorage.setItem("authToken", response.data.token);
        localStorage.setItem('isLoggedIn', 'true')
        localStorage.setItem("userId", response.data.id);
        if (email && !localStorage.getItem("userEmail")) {
          localStorage.setItem("userEmail", email);
        }
        // Trigger storage event for the Header to detect changes
        window.dispatchEvent(new Event("storage"));

        // Redirect to dashboard
        window.location.href = "/engagements";
      } else {
        toast.error(response.data.message, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      const errorMessage = error.response?.data?.message || "Something went wrong. Please try again.";
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="credential-page">
      <Container fluid>
        <Row className="justify-content-md-center" style={{ minHeight: "100vh" }}>

          <Col lg={6} className="p-0">
            <div className="left-side d-md-flex align-items-center justify-content-center bg-light">
              <QuoteSlider quotes={quotes} />
            </div>
          </Col>

          <Col lg={6} className="right-side my-auto">
            <div className="mb-4 card-title text-center">
              <h5 className="fw-bolder text-dark" style={{ fontSize: '30px' }}>Two Factor Authentication</h5>
              <p className="my-4 text-light" style={{ textAlign: 'left', fontSize: '18px' }}>
                A verification code has been sent to your email. This code will be valid for 15 minutes.
              </p>
            </div>

            <Form onSubmit={handleSubmit} className="otp-screen">
              <Form.Label>Enter Code*</Form.Label>
              <div className="d-flex custom-otp-container  mb-2">
                <OtpInput
                  value={otp}
                  onChange={handleOtpChange}
                  numInputs={5}
                  separator={<span style={{ width: "8px" }}></span>}
                  renderSeparator={<span>&nbsp;&nbsp;&nbsp;</span>}
                  isInputNum={true}
                  shouldAutoFocus={true}
                  inputStyle={{
                    background: "#E7EFF8",
                    border: "0",
                    borderRadius: "8px",
                    height: "61px",
                    width: "70px",
                    fontSize: "20px",
                    color: "#677289",
                    fontWeight: "700",
                    caretColor: "blue",
                    textAlign: "center",
                  }}
                  focusStyle={{
                    border: "none",
                    outline: "none",
                  }}
                  renderInput={(props) => <input {...props} />}
                />
              </div>
              <Button
                variant="primary"
                className="mt-4 border-0 text-white font-16 px-5 py-3 w-100 fw-bold"
                type="submit"
                disabled={loading || submitCooldown > 0}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Verifying...
                  </>
                ) : submitCooldown > 0 ? `Verify (${submitCooldown}s)` : "Verify"}
              </Button>

              <p className="mt-5 text-center text-light">
                Haven't received Two Factor Authentication?{" "}
                <span
                  className="text-secondary fw-bold"
                  style={{
                    cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
                    color: resendCooldown > 0 ? "#888" : "inherit",
                  }}
                  onClick={resendCooldown > 0 ? undefined : handleResendOTP}
                >
                  {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : "Resend"}
                </span>
              </p>
            </Form>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

export default TwoFactorOTP;